/**
 * DocumentsPage — archivio dei documenti definitivi (Documenti di Vendita/DDT)
 * generati quando il magazzino spedisce la merce e l'ordine viene chiuso.
 * Visibilità: admin/magazzino tutti, agente i propri clienti, cliente i propri.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { FolderOpen, FileDown, Search, X, Send, Mail, MessageCircle, Paperclip, Share2, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { API } from "../utils/format";
import { formatMoney } from "../utils/currency";
import { openAuthenticatedPdf } from "../utils/pdf";
import { shareWhatsAppWithPdf, downloadBlob, canShareFiles, shareFileNative } from "../utils/share";
import { DatePickerIT } from "../components/DatePickerIT";
import { useTableSort, SortableTh } from "../components/tableSort";
import DdtSequenceDialog from "../components/DdtSequenceDialog";
import SendDocEmailDialog from "../components/SendDocEmailDialog";

const fmtDate = (iso) => (iso ? new Date(iso.length === 10 ? iso + "T00:00:00" : iso).toLocaleDateString("it-IT") : "—");

const SHARE_SUPPORTED = canShareFiles();

/** Nome file condivisione: "DDT n. - nome cliente - ggmmaa" (es. "DDT 1560 - AC SRL - 230726.pdf") */
const docShareFilename = (d, ext = "pdf") => {
  const rawNum = (d.document_reference || d.doc_number || "").replace(/[/\\]/g, "-").trim();
  const prefix = rawNum.toUpperCase().startsWith("DDT") ? rawNum : `DDT ${rawNum || "s.n."}`;
  const name = ((d.customer_snapshot || {}).business_name || "Cliente").replace(/[^\w\sÀ-ÿ.-]/g, "").trim().slice(0, 25);
  const dt = d.doc_date ? new Date(d.doc_date.length === 10 ? d.doc_date + "T00:00:00" : d.doc_date) : new Date();
  const dd = `${String(dt.getDate()).padStart(2, "0")}${String(dt.getMonth() + 1).padStart(2, "0")}${String(dt.getFullYear()).slice(2)}`;
  return `${prefix} - ${name} - ${dd}.${ext}`;
};

export default function DocumentsPage({ currentUser, company }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [customersMap, setCustomersMap] = useState({});
  const [agentsMap, setAgentsMap] = useState({});
  const [seqOpen, setSeqOpen] = useState(false);
  const [mailDoc, setMailDoc] = useState(null);
  const sort = useTableSort();
  const role = currentUser?.role;

  useEffect(() => {
    if (role === "customer") return;
    axios.get(`${API}/customers`).then((r) => {
      const map = {};
      (r.data || []).forEach((c) => { map[c.id] = c; });
      setCustomersMap(map);
    }).catch(() => {});
    if (role === "admin" || role === "warehouse") {
      axios.get(`${API}/agents`).then((r) => {
        const map = {};
        (r.data || []).forEach((a) => { map[a.id] = a.name; });
        setAgentsMap(map);
      }).catch(() => {});
    }
  }, [role]);

  const fetchPdfBlob = async (d) => {
    const r = await axios.get(`${API}/orders/${d.order_id}/pdf?for_customer=true`, { responseType: "blob" });
    return new Blob([r.data], { type: "application/pdf" });
  };

  const shareWhatsApp = async (d) => {
    try {
      const blob = await fetchPdfBlob(d);
      const cust = customersMap[d.customer_id];
      const text = `Buongiorno, in allegato il documento ${d.doc_number}${d.order_number ? ` (ordine ${d.order_number})` : ""} — Giotto Carni. Grazie!`;
      const res = await shareWhatsAppWithPdf({ text, blob, filename: docShareFilename(d), phone: cust?.phone });
      if (res === "shared") toast.success("Documento condiviso con PDF allegato");
    } catch (e) {
      toast.error("Errore condivisione documento");
    }
  };

  // Condivisione nativa (pannello del telefono/iPad) del PDF documento
  const shareDocumentPdf = async (d) => {
    try {
      const blob = await fetchPdfBlob(d);
      const filename = docShareFilename(d);
      await shareFileNative({ blob, filename, title: filename });
    } catch {
      toast.error("Errore condivisione documento");
    }
  };

  // Condivisione nativa del DDT originale allegato (scan/PDF caricato dal magazzino)
  const shareDdtOriginal = async (d) => {
    try {
      const r = await axios.get(`${API}/files/${d.ddt_attachment.path}`, { responseType: "blob" });
      const orig = d.ddt_attachment.filename || d.ddt_attachment.path || "";
      const extRaw = (orig.split(".").pop() || "pdf").toLowerCase();
      const ext = /^[a-z0-9]{2,4}$/.test(extRaw) ? extRaw : "pdf";
      const filename = docShareFilename(d, ext);
      await shareFileNative({ blob: r.data, filename, title: filename });
    } catch {
      toast.error("Errore condivisione DDT");
    }
  };

  const shareEmail = async (d) => {
    try {
      const blob = await fetchPdfBlob(d);
      downloadBlob(blob, docShareFilename(d));
      const cust = customersMap[d.customer_id];
      const subject = `Documento ${d.doc_number} — Giotto Carni`;
      const body = `Buongiorno,\n\nin allegato il documento ${d.doc_number}${d.order_number ? ` relativo all'ordine ${d.order_number}` : ""}.\n\nCordiali saluti,\nGiotto Carni`;
      window.location.href = `mailto:${cust?.email || ""}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      toast.info("PDF scaricato — allegalo all'email appena aperta");
    } catch (e) {
      toast.error("Errore preparazione email");
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { with_order: true };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await axios.get(`${API}/sales-documents`, { params });
      setDocs(r.data || []);
    } catch (e) {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const q = search.trim().toLowerCase();
  const filtered = sort.sortList(
    docs.filter((d) => {
      if (!q) return true;
      return (
        (d.doc_number || "").toLowerCase().includes(q) ||
        (d.order_number || "").toLowerCase().includes(q) ||
        (d.document_reference || "").toLowerCase().includes(q) ||
        (d.invoice_ref || "").toLowerCase().includes(q) ||
        ((d.customer_snapshot || {}).business_name || "").toLowerCase().includes(q)
      );
    }),
    {
      customer: (d) => (d.customer_snapshot || {}).business_name || "",
      agent: (d) => agentsMap[customersMap[d.customer_id]?.agent_id] || agentsMap[d.agent_id] || "",
    }
  );

  return (
    <div className="space-y-5" data-testid="documents-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-2">
            <FolderOpen className="w-6 h-6 text-[#8A1C2A]" /> Documenti
          </h1>
          <p className="text-stone-500 text-sm mt-1">
            Documenti definitivi degli ordini spediti — scarica il PDF con pesi e importi finali
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(role === "admin" || role === "warehouse") && (
            <Button
              variant="outline"
              onClick={() => setSeqOpen(true)}
              className="border-[#8A1C2A]/40 text-[#8A1C2A] hover:bg-[#8A1C2A]/5"
              data-testid="ddt-sequence-btn"
            >
              <ListChecks className="w-4 h-4 mr-2" /> Controllo progressivo DDT
            </Button>
          )}
          <Badge variant="outline" className="text-stone-600 border-stone-300" data-testid="documents-count">
            {filtered.length} documenti
          </Badge>
        </div>
      </div>

      <DdtSequenceDialog open={seqOpen} onClose={() => setSeqOpen(false)} />

      <Card className="p-3 bg-white border-stone-200 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per numero, ordine, DDT o cliente..."
            className="pl-9 h-9"
            data-testid="documents-search"
          />
        </div>
        <div className="flex items-center gap-2">
          <DatePickerIT value={dateFrom} onChange={setDateFrom} placeholder="Dal" data-testid="documents-date-from" />
          <DatePickerIT value={dateTo} onChange={setDateTo} placeholder="Al" data-testid="documents-date-to" />
          {(dateFrom || dateTo || search) && (
            <Button variant="ghost" size="sm" onClick={() => { setDateFrom(""); setDateTo(""); setSearch(""); }} data-testid="documents-clear-filters">
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </Card>

      <Card className="p-0 overflow-hidden bg-white border-stone-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-100 text-left text-xs uppercase text-stone-600">
              <tr>
                <SortableTh label="Documento" sortId="doc_number" sort={sort} />
                <SortableTh label="Rif. DDT" sortId="document_reference" sort={sort} />
                <SortableTh label="Rif. Fattura" sortId="invoice_ref" sort={sort} />
                <SortableTh label="Ordine" sortId="order_number" sort={sort} />
                <SortableTh label="Data" sortId="doc_date" sort={sort} />
                {role !== "customer" && <SortableTh label="Cliente" sortId="customer" sort={sort} />}
                {(role === "admin" || role === "warehouse") && <SortableTh label="Agente" sortId="agent" sort={sort} />}
                <SortableTh label="Totale" sortId="amount_total" sort={sort} align="right" />
                <th className="p-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="p-6 text-center text-stone-500">Caricamento...</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-10 text-center text-stone-500 italic" data-testid="documents-empty">
                    Nessun documento — i documenti definitivi appaiono qui quando il magazzino spedisce gli ordini
                  </td>
                </tr>
              )}
              {!loading && filtered.map((d) => (
                <tr
                  key={d.id}
                  className="border-t border-stone-100 hover:bg-stone-50 cursor-pointer"
                  onClick={() => d.order_id && openAuthenticatedPdf(`/orders/${d.order_id}/pdf?inline=true`)}
                  title="Clicca per visualizzare il documento"
                  data-testid={`document-row-${d.doc_number}`}
                >
                  <td className="p-3 font-mono font-semibold text-stone-900">{d.doc_number}</td>
                  <td className="p-3 text-stone-600">{d.document_reference || "—"}</td>
                  <td className="p-3" data-testid={`document-invoice-ref-${d.doc_number}`}>
                    {d.invoice_ref
                      ? <span className="inline-flex items-center bg-emerald-50 border border-emerald-200 text-emerald-800 rounded px-1.5 py-0.5 text-xs font-mono font-semibold">Fatt. {d.invoice_ref}</span>
                      : <span className="text-stone-400 text-xs">—</span>}
                  </td>
                  <td className="p-3 font-mono text-xs text-stone-600">{d.order_number || "—"}</td>
                  <td className="p-3 text-stone-600">{fmtDate(d.doc_date)}</td>
                  {role !== "customer" && (
                    <td className="p-3 text-stone-800">{(d.customer_snapshot || {}).business_name || "—"}</td>
                  )}
                  {(role === "admin" || role === "warehouse") && (
                    <td className="p-3 text-xs text-stone-600" data-testid={`document-agent-${d.doc_number}`}>
                      {agentsMap[customersMap[d.customer_id]?.agent_id] || agentsMap[d.agent_id] || "—"}
                    </td>
                  )}
                  <td className="p-3 text-right font-semibold">{formatMoney(d.amount_total, company)}</td>
                  <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1.5">
                      {d.ddt_attachment?.path && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.open(`${API}/files/${d.ddt_attachment.path}`, "_blank")}
                            className="h-8 border-stone-300 text-stone-700 hover:bg-stone-50"
                            title={`DDT originale: ${d.ddt_attachment.filename || ""}`}
                            data-testid={`document-ddt-original-${d.doc_number}`}
                          >
                            <Paperclip className="w-3.5 h-3.5 mr-1" /> DDT
                          </Button>
                          {SHARE_SUPPORTED && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => shareDdtOriginal(d)}
                              className="h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                              title="Condividi DDT originale (WhatsApp, Email, AirDrop...)"
                              data-testid={`document-ddt-share-${d.doc_number}`}
                            >
                              <Share2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openAuthenticatedPdf(`/orders/${d.order_id}/pdf?inline=true`)}
                        className="h-8 border-[#8A1C2A]/40 text-[#8A1C2A] hover:bg-[#8A1C2A]/5"
                        data-testid={`document-pdf-${d.doc_number}`}
                      >
                        <FileDown className="w-3.5 h-3.5 mr-1" /> PDF
                      </Button>
                      {SHARE_SUPPORTED && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => shareDocumentPdf(d)}
                          className="h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          title="Condividi documento (WhatsApp, Email, AirDrop...)"
                          data-testid={`document-share-${d.doc_number}`}
                        >
                          <Share2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {role !== "customer" && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" className="h-8 bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid={`document-send-${d.doc_number}`}>
                              <Send className="w-3.5 h-3.5 mr-1" /> Invia
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setMailDoc(d)} data-testid={`document-send-internal-${d.doc_number}`}>
                              <Mail className="w-4 h-4 mr-2 text-[#8A1C2A]" /> Email dal gestionale (tua casella)
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => shareWhatsApp(d)} data-testid={`document-send-whatsapp-${d.doc_number}`}>
                              <MessageCircle className="w-4 h-4 mr-2 text-green-600" /> WhatsApp (PDF allegato)
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => shareEmail(d)} data-testid={`document-send-email-${d.doc_number}`}>
                              <Mail className="w-4 h-4 mr-2 text-blue-600" /> Email (programma esterno){customersMap[d.customer_id]?.email ? ` — ${customersMap[d.customer_id].email}` : ""}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {mailDoc && (
        <SendDocEmailDialog
          open={!!mailDoc}
          onClose={() => setMailDoc(null)}
          title={`Invia documento ${mailDoc.doc_number}`}
          attachmentLabel={`Documento ${mailDoc.doc_number} (PDF)${mailDoc.ddt_attachment?.path ? " + DDT originale" : ""}`}
          endpoint={`${API}/docs/send-email`}
          extraPayload={{
            doc_refs: [
              { type: "order", id: mailDoc.order_id },
              ...(mailDoc.ddt_attachment?.path ? [{ type: "ddt", id: mailDoc.id }] : []),
            ],
          }}
          subjectDefault={`Documento ${mailDoc.doc_number} — Giotto Carni`}
          bodyDefault={`Buongiorno,\n\nin allegato il documento ${mailDoc.doc_number}${mailDoc.order_number ? ` relativo all'ordine ${mailDoc.order_number}` : ""}.\n\nCordiali saluti,\nGiotto Carni`}
          presetEmails={customersMap[mailDoc.customer_id]?.email ? [customersMap[mailDoc.customer_id].email] : []}
        />
      )}
    </div>
  );
}
